// ============================================================================
// ADR-0024 — save Corporate Facts by section
// ============================================================================
// Several screens edit the one facts record. Each sends ONLY the sections
// (top-level data keys; "__agm" for the AGM columns) it changed, with the
// stamps it read them at; the server refuses a save only when one of THOSE
// sections changed meanwhile, and merges everything else. Mirrors prima-api
// CorporateFactsSections.

import type {
  CorporateFacts,
  CorporateFactsData,
  SaveCorporateFactsSectionsRequest,
} from 'api/types/fsa/corporateFacts.types';

export const AGM_KEY = '__agm';

/** The record's data as read (same rule as useFsaCorporateFacts.parseCorporateFactsData; kept here so this module has no API imports). */
function parseData(raw: string | null | undefined): CorporateFactsData {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export interface AgmColumns {
  agmNumber: number | null;
  agmDate: string | null;
  agmTime: string | null;
  agmVenue: string | null;
}

/** The sections whose value differs between what was read and the draft (removed = null). */
export function changedSections(original: CorporateFactsData, draft: CorporateFactsData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(original), ...Object.keys(draft)]);
  keys.forEach(k => {
    const before = original[k];
    const after = draft[k];
    if (JSON.stringify(before) !== JSON.stringify(after)) out[k] = after === undefined ? null : after;
  });
  return out;
}

const agmOf = (f: CorporateFacts | null | undefined): AgmColumns => ({
  agmNumber: f?.agmNumber ?? null,
  agmDate: f?.agmDate ? f.agmDate.slice(0, 10) : null,
  agmTime: f?.agmTime || null,
  agmVenue: f?.agmVenue || null,
});

/**
 * The request saving only what changed: the data sections that differ from
 * the record as read, and the AGM columns when `agm` is given and differs.
 */
export function sectionSaveRequest(
  facts: CorporateFacts | null | undefined,
  draft: CorporateFactsData,
  agm?: AgmColumns,
): SaveCorporateFactsSectionsRequest {
  const sections = changedSections(parseData(facts?.data), draft);
  const stamps = facts?.sectionStamps ?? {};
  const expected: Record<string, string | null> = {};
  Object.keys(sections).forEach(k => { expected[k] = stamps[k] ?? null; });
  const request: SaveCorporateFactsSectionsRequest = { sections, expected };
  if (agm && JSON.stringify(agm) !== JSON.stringify(agmOf(facts))) {
    request.agm = agm;
    expected[AGM_KEY] = stamps[AGM_KEY] ?? null;
  }
  return request;
}

/** True when the request has nothing to save. */
export const isEmptySave = (r: SaveCorporateFactsSectionsRequest) => Object.keys(r.sections).length === 0 && !r.agm;
